import { Box, Container, Heading, Text, VStack } from "@chakra-ui/react"
import { Helmet } from "react-helmet"

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - SpearBI</title>
        <meta name="description" content="Privacy policy for the SpearBI Reporting System" />
      </Helmet>
      <Container maxW="4xl" py={8}>
        <VStack spacing={6} align="stretch">
          <Heading as="h1" size="xl">
            Privacy Policy
          </Heading>
          <Text color="gray.500">Effective Date: May 22, 2025</Text>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              Scope
            </Heading>
            <Text>
              This Privacy Policy explains how SpearBI ("we", "us") collects, uses, discloses and protects personal
              information when you use the SpearBI Reporting System ("Service").
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              Information We Collect
            </Heading>
            <VStack spacing={2} align="stretch" pl={4}>
              <Text>
                <strong>Registration Data:</strong> name, email address, organization, role, phone number.
              </Text>
              <Text>
                <strong>Usage Data:</strong> log data, IP address, browser type, pages visited, time stamps.
              </Text>
              <Text>
                <strong>Uploaded Data:</strong> any files or records you import (e.g., sales figures, strike rates).
              </Text>
              <Text>
                <strong>Cookies & Tracking:</strong> session cookies and analytics cookies to improve performance.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              How We Use Information
            </Heading>
            <VStack spacing={2} align="stretch" pl={4}>
              <Text>• To provide and maintain the Service.</Text>
              <Text>• To authenticate and manage your Account.</Text>
              <Text>• To analyse usage and improve features, reports and UX.</Text>
              <Text>• To send you transactional emails (e.g., password resets, account notifications).</Text>
              <Text>• To comply with legal obligations.</Text>
            </VStack>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              Data Sharing & Disclosure
            </Heading>
            <VStack spacing={2} align="stretch" pl={4}>
              <Text>
                <strong>Service Providers:</strong> we may share information with third-party vendors who perform
                services on our behalf (hosting, analytics, email).
              </Text>
              <Text>
                <strong>Legal Requirements:</strong> we may disclose information to comply with applicable laws or
                respond to lawful requests.
              </Text>
              <Text>
                <strong>Business Transfers:</strong> in connection with a merger, acquisition, or sale of assets, your
                Data may be transferred.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              Data Security
            </Heading>
            <Text>
              We implement reasonable technical and organizational measures (encryption in transit, access controls) to
              protect your information. However, no system is 100% secure—use strong, unique passwords and secure your
              end.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              Data Retention
            </Heading>
            <Text>
              We retain personal information as long as your Account is active plus any additional period needed to
              comply with legal obligations. You may request deletion of your Account and data at any time (see Section
              9).
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              Your Rights
            </Heading>
            <VStack spacing={2} align="stretch" pl={4}>
              <Text>
                <strong>Access & Correction:</strong> you can view and update your Account information anytime.
              </Text>
              <Text>
                <strong>Deletion:</strong> you may request erasure of your personal information, subject to legal
                retention requirements.
              </Text>
              <Text>
                <strong>Objection & Restriction:</strong> you can object to certain processing (e.g., analytics).
              </Text>
              <Text>
                <strong>Data Portability:</strong> you may request a machine-readable copy of your data.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              Cookies & Tracking
            </Heading>
            <Text>
              You can disable cookies via your browser settings, but this may impact Service functionality. For more on
              cookies we use, see our Cookie Policy (if applicable).
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              Contact & Complaints
            </Heading>
            <Text>
              For privacy inquiries or to exercise your rights, please contact privacy@spearbi.org. If you're not
              satisfied with our response, you may lodge a complaint with the Information Regulator of South Africa.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={2}>
              Changes to This Policy
            </Heading>
            <Text>
              We may update this Privacy Policy. If material changes occur, we'll notify you at least 14 days before
              they take effect. Continued use after the effective date means you accept the changes.
            </Text>
          </Box>
        </VStack>
      </Container>
    </>
  )
}
